import React, { useEffect, useRef, useState } from "react";
import { Alert, Spin } from "antd";
import { fetchProjectDocumentationFileBlob } from "../../../app/utils/common/projectDocumentationFile";

/**
 * Read-only HTML preview for project documentation.
 * Rendered in a fully sandboxed iframe so scripts, forms and navigation in the uploaded file cannot run.
 */
const ProjectDocumentationHtmlViewer = ({ documentId, fileDocumentId, onLoadError, compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [html, setHtml] = useState("");
  const [loadError, setLoadError] = useState(null);
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    setHtml("");

    (async () => {
      try {
        const blob = await fetchProjectDocumentationFileBlob(documentId, fileDocumentId);
        const text = await blob.text();
        if (!cancelled) setHtml(text);
      } catch (e) {
        if (!cancelled) {
          setLoadError(e?.message || "Could not load the HTML file.");
          onLoadErrorRef.current?.(e);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [documentId, fileDocumentId]);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  if (loadError) {
    return <Alert type="error" showIcon message={loadError} />;
  }

  return (
    <iframe
      title="HTML preview"
      sandbox=""
      referrerPolicy="no-referrer"
      srcDoc={html}
      className="w-full bg-white rounded-[8px] border border-solid border-[#e7ebf6]"
      style={{ height: compact ? "min(50vh, 400px)" : "calc(100vh - 240px)" }}
    />
  );
};

export default ProjectDocumentationHtmlViewer;
